import React from "react";
import {
  Badge,
  Box,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import PageHeader from "../../shared/components/PageHeader";

// Sentinel value for the school-year Select's "Start New Year" entry: it is an
// action, not a selectable year, so onChange routes it to onStartNewYear and
// leaves the current selection untouched.
const START_NEW_YEAR = "__start_new_year__";

// Page-header definition for the legacy v1 Admissions pages (Students, Setup,
// Dashboard, Enrollments, Actions). The legacy header carried two controls
// besides the tabs - an Admissions Active toggle and the school-year picker -
// and both move into the v2 header's right-aligned actions slot here. Each is
// driven by data + callbacks handed across the v1/v2 bridge; v1 still owns the
// school-year list, the active-status request, and the Start New Year drawer.
// A tab may carry a pending count (Actions, advanced admissions only) rendered
// as a badge on its label.
export default function AdmissionsHeader({
  tabs = [],
  activeKey,
  onNavigate,
  admissionsActive,
  onToggleActive,
  schoolYears = [],
  selectedSchoolYearId,
  onSchoolYearChange,
  canStartNewYear,
  onStartNewYear,
  selectDisabled,
}) {
  // Match against a value the rendered MenuItems actually carry, so the Select
  // does not warn while v1 is still loading the school-year list.
  const selectValue = schoolYears.some((y) => y.id === selectedSchoolYearId)
    ? selectedSchoolYearId
    : "";

  const handleSelect = (next) => {
    if (next === START_NEW_YEAR) onStartNewYear();
    else onSchoolYearChange(next);
  };

  const schoolYearSelect = (
    <Select
      size="small"
      value={selectValue}
      onChange={(e) => handleSelect(e.target.value)}
      disabled={selectDisabled}
      displayEmpty
      inputProps={{ "aria-label": "School year" }}
      renderValue={(v) =>
        schoolYears.find((y) => y.id === v)?.name || "Select school year"
      }
      // Compact chip beside the title, matching the Subjects term picker: no fixed
      // width (sizes to the selected year), tighter padding/font than size="small",
      // and the arrow pulled to the edge with the right padding set to clear it.
      sx={{
        flexShrink: 0,
        fontSize: 13,
        "& .MuiSelect-select": { py: 0.5, pl: 1, pr: "22px" },
        "& .MuiSelect-icon": { right: 0 },
      }}
    >
      {canStartNewYear && (
        <MenuItem value={START_NEW_YEAR}>
          <Typography color="primary">Start New Year</Typography>
        </MenuItem>
      )}
      {schoolYears.map((y) => (
        <MenuItem key={y.id} value={y.id}>
          {y.name}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <PageHeader
      title={
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}
        >
          <Typography
            sx={{ fontSize: 24, lineHeight: 1.2, whiteSpace: "nowrap" }}
          >
            Admissions
          </Typography>
          {schoolYearSelect}
        </Box>
      }
      // A null activeKey leaves every tab unselected (e.g. on a sub-page no tab
      // matches); PageHeader maps an unmatched activeTab to no selection. A tab
      // with a positive count (Actions) gets a badge on its label; at zero the
      // label renders plain, no badge.
      tabs={tabs.map((t) => ({
        key: t.key,
        label: t.count ? (
          <Badge badgeContent={t.count} color="error">
            {t.label}
          </Badge>
        ) : (
          t.label
        ),
        onClick: t.key === activeKey ? undefined : () => onNavigate(t.url),
      }))}
      activeTab={activeKey}
      actions={
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", flexShrink: 0 }}
        >
          <Typography sx={{ whiteSpace: "nowrap" }}>
            Admissions Active
          </Typography>
          <Switch
            checked={admissionsActive === true}
            onChange={(e) => onToggleActive(e.target.checked)}
            slotProps={{ input: { "aria-label": "Admissions Active" } }}
          />
        </Stack>
      }
    />
  );
}
